import { useEffect, useMemo } from "react";

import { getAuthMe, getStoredAuthToken, postTelegramAuth } from "../api/api";
import { useAuth } from "../context/AuthContext";

/**
 * Дублирует safe area из Telegram.WebApp в CSS-переменные на <html>.
 * На iPhone после fullscreen CSS-инъекция иногда даёт 0 для --tg-content-safe-area-inset-top —
 * контент наезжает на кнопку «Закрыть»; подстраховываем минимальным inset для iPhone.
 */
function applyTelegramSafeAreaCssVars() {
  const tg = window.Telegram?.WebApp;
  if (!tg) return;

  const root = document.documentElement;
  const c = tg.contentSafeAreaInset;
  const s = tg.safeAreaInset;

  let top = Number(c?.top);
  if (!Number.isFinite(top)) top = 0;
  let right = Number(c?.right);
  if (!Number.isFinite(right)) right = 0;
  let bottom = Number(c?.bottom);
  if (!Number.isFinite(bottom)) bottom = 0;
  let left = Number(c?.left);
  if (!Number.isFinite(left)) left = 0;

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIPhone = /iPhone/.test(ua) && !/iPad/.test(ua);
  root.classList.toggle("tg-platform-iphone", isIPhone);
  /* Полоса под кнопкой «Закрыть» в fullscreen: TG иногда даёт заниженный top — держим минимум ~58px */
  if (isIPhone) {
    top = Math.max(top, 58);
  }

  root.style.setProperty("--tg-content-safe-area-inset-top", `${top}px`);
  root.style.setProperty("--tg-content-safe-area-inset-right", `${right}px`);
  root.style.setProperty("--tg-content-safe-area-inset-bottom", `${bottom}px`);
  root.style.setProperty("--tg-content-safe-area-inset-left", `${left}px`);

  if (s) {
    const st = Number(s.top);
    const sr = Number(s.right);
    const sb = Number(s.bottom);
    const sl = Number(s.left);
    root.style.setProperty("--tg-safe-area-inset-top", `${Number.isFinite(st) ? st : 0}px`);
    root.style.setProperty("--tg-safe-area-inset-right", `${Number.isFinite(sr) ? sr : 0}px`);
    root.style.setProperty("--tg-safe-area-inset-bottom", `${Number.isFinite(sb) ? sb : 0}px`);
    root.style.setProperty("--tg-safe-area-inset-left", `${Number.isFinite(sl) ? sl : 0}px`);
  }
}

/** Fullscreen / жесты — только мобильные клиенты TG; на tdesktop/web/Qt WebEngine часто глючит или даёт пустой экран. */
function shouldUseTelegramMobileFullscreenGestures(tg) {
  const p = String(tg?.platform || "").toLowerCase();
  if (p === "ios" || p === "android") return true;
  if (p && p !== "unknown") return false;
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  return /Android|iPhone|iPad|iPod/i.test(ua);
}

export function useAuthUser() {
  const { authUser } = useAuth();
  return authUser;
}

export function useIsTelegramAdmin() {
  const authUser = useAuthUser();

  return useMemo(() => {
    return !!authUser?.is_admin;
  }, [authUser]);
}

export function useIsTelegramStaff() {
  const authUser = useAuthUser();

  return useMemo(() => {
    return !!authUser?.is_admin || !!authUser?.is_hostess;
  }, [authUser]);
}

export function useTelegramWebApp() {
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg) return;

    const applySafeArea = () => applyTelegramSafeAreaCssVars();
    const mobileFs = shouldUseTelegramMobileFullscreenGestures(tg);

    try {
      tg.ready();
      tg.expand();
    } catch (e) {
      console.warn("Telegram WebApp ready/expand:", e);
    }

    /* Bot API 8.0+: только на телефонах; на десктопе TD/WebEngine иногда пустой экран после fullscreen. */
    if (mobileFs) {
      try {
        if (typeof tg.requestFullscreen === "function") {
          tg.requestFullscreen();
        }
      } catch (e) {
        console.warn("Telegram requestFullscreen:", e);
      }
      try {
        if (typeof tg.disableVerticalSwipes === "function") {
          tg.disableVerticalSwipes();
        }
      } catch (e) {
        console.warn("Telegram disableVerticalSwipes:", e);
      }
    }

    try {
      if (typeof tg.setHeaderColor === "function") {
        tg.setHeaderColor("#1d1d1b");
      }
      if (typeof tg.setBackgroundColor === "function") {
        tg.setBackgroundColor("#1d1d1b");
      }
    } catch (e) {
      console.warn("Telegram WebApp colors:", e);
    }

    applySafeArea();
    requestAnimationFrame(applySafeArea);
    requestAnimationFrame(() => requestAnimationFrame(applySafeArea));
    const safeAreaTimeouts = [0, 120, 450].map((ms) =>
      setTimeout(applySafeArea, ms),
    );

    const safeAreaEvents = [
      "contentSafeAreaChanged",
      "safeAreaChanged",
      "viewportChanged",
      "fullscreenChanged",
    ];
    safeAreaEvents.forEach((ev) => {
      if (typeof tg.onEvent !== "function") return;
      try {
        tg.onEvent(ev, applySafeArea);
      } catch (e) {
        console.warn("Telegram onEvent:", ev, e);
      }
    });

    return () => {
      document.documentElement.classList.remove("tg-platform-iphone");
      safeAreaTimeouts.forEach((id) => clearTimeout(id));
      safeAreaEvents.forEach((ev) => {
        if (typeof tg.offEvent !== "function") return;
        try {
          tg.offEvent(ev, applySafeArea);
        } catch (e) {
          console.warn("Telegram offEvent:", ev, e);
        }
      });
    };
  }, []);
}

export function useTelegramBackendAuth() {
  const { setAuth } = useAuth();

  useEffect(() => {
    let cancelled = false;

    const syncProfileForBirthdayAndBalance = () => {
      const t = getStoredAuthToken();
      if (!t) return;
      getAuthMe()
        .then((u) => {
          if (cancelled || !u?.id) return;
          setAuth(u, t);
        })
        .catch(() => {});
    };

    const runAuth = () => {
      const initData = window.Telegram?.WebApp?.initData;
      if (!initData) return false;
      postTelegramAuth(initData)
        .then((data) => {
          if (cancelled || !data?.user) return;
          const token = data.sessionToken ?? data.session_token ?? null;
          setAuth(data.user, token);
          /* Повторный запрос профиля: на бэке GET /auth/me начисляет ежегодный подарок на ДР вовремя. */
          syncProfileForBirthdayAndBalance();
        })
        .catch((err) => {
          if (!cancelled) console.warn("Telegram auth API:", err.message || err);
        });
      return true;
    };

    if (runAuth()) {
      return () => {
        cancelled = true;
      };
    }

    const interval = window.setInterval(() => {
      if (cancelled) return;
      if (runAuth()) window.clearInterval(interval);
    }, 400);
    const stop = window.setTimeout(() => window.clearInterval(interval), 10000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.clearTimeout(stop);
    };
  }, [setAuth]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const t = getStoredAuthToken();
      if (!t) return;
      getAuthMe()
        .then((u) => {
          if (!u?.id) return;
          setAuth(u, t);
        })
        .catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [setAuth]);
}
